/** A small coloured chip. The five tones are the only status colours in use. */
import type { ReactNode } from 'react';

export type BadgeTone = 'green' | 'amber' | 'red' | 'neutral' | 'teal';

export interface BadgeProps {
  children: ReactNode;
  tone?: BadgeTone;
  /** A small dot before the label, for stage and status chips. */
  dot?: boolean;
  /** Muted, smaller: for a secondary qualifier such as "advisory". */
  quiet?: boolean;
  title?: string;
  className?: string;
}

export function Badge({ children, tone = 'neutral', dot = false, quiet = false, title, className }: BadgeProps) {
  const classes = ['badge', `badge-${tone}`, quiet ? 'badge-quiet' : '', className]
    .filter(Boolean)
    .join(' ');
  return (
    <span className={classes} title={title}>
      {dot && <i className="badge-dot" aria-hidden="true" />}
      {children}
    </span>
  );
}

/** Tone for a pipeline stage, so every screen colours the stages the same way. */
export function stageTone(stage: string | null | undefined): BadgeTone {
  switch (stage) {
    case 'closed_won':
      return 'green';
    case 'closed_lost':
      return 'red';
    case 'contracting':
    case 'negotiating':
      return 'teal';
    case 'quoted':
      return 'amber';
    default:
      return 'neutral';
  }
}

/** Tone for a quote version status. */
export function quoteStatusTone(status: string | null | undefined): BadgeTone {
  switch (status) {
    case 'issued':
    case 'accepted':
      return 'green';
    case 'approved':
      return 'teal';
    case 'pending_approval':
      return 'amber';
    case 'declined':
    case 'withdrawn':
      return 'red';
    default:
      return 'neutral';
  }
}

/** Tone for a reference-release status. */
export function releaseStatusTone(status: string | null | undefined): BadgeTone {
  switch (status) {
    case 'published':
      return 'green';
    case 'validating':
      return 'amber';
    case 'retired':
      return 'neutral';
    default:
      return 'teal';
  }
}

/** Tone for a finding severity. A blocker is always red. */
export function severityTone(severity: string | null | undefined): BadgeTone {
  switch (severity) {
    case 'blocker':
      return 'red';
    case 'warning':
      return 'amber';
    default:
      return 'neutral';
  }
}

export default Badge;
